import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  CalendarDays,
  Building2,
  FileText,
  BadgeEuro,
  Clock3,
  ArrowRightCircle,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Wallet,
  Briefcase,
  MessageSquare,
  Target,
  TrendingUp,
} from 'lucide-react';
import {
  getProcedureLink,
  getProcedureTypeLabel,
  getProjectSourceLabel,
} from '../../utils/procedureLinks';
import { slugify } from '../../utils/slugify';

const statusColors = {
  'Anunt de intentie': 'bg-amber-100 text-amber-700 border-amber-300',
  'In planificare': 'bg-yellow-100 text-yellow-700 border-yellow-300',
  'Proiectare in licitatie': 'bg-blue-100 text-blue-700 border-blue-300',
  'Executie in licitatie': 'bg-indigo-100 text-indigo-700 border-indigo-300',
  'Supervizare in licitatie': 'bg-cyan-100 text-cyan-700 border-cyan-300',
  'Partial atribuit': 'bg-green-100 text-green-700 border-green-300',
  'In desfasurare': 'bg-emerald-100 text-emerald-700 border-emerald-300',
  'In licitatie': 'bg-purple-100 text-purple-700 border-purple-300',
};

const phaseIcons = {
  Intentie: '📋',
  Planificare: '📅',
  Proiectare: '📐',
  Executie: '🏗️',
  Dirigentie: '👷',
  Supervizare: '🔍',
  Dotare: '🛋️',
  Furnizare: '📦',
  Atribuire: '✅',
  Altele: '📄',
};

const ProcedureRow = ({ proc, currency, hasAccess }) => {
  const link = getProcedureLink(proc.mongoId, proc.collectionSource, proc.procedureTitle || proc.auctionTitle);
  const typeLabel = getProcedureTypeLabel(proc.collectionSource);

  // Truncate title to 160 chars for free users
  const displayTitle = hasAccess
    ? proc.procedureTitle
    : proc.procedureTitle?.length > 160
      ? proc.procedureTitle.substring(0, 160) + '...'
      : proc.procedureTitle;

  return (
    <div className={`bg-white border rounded-xl px-4 py-3 hover:border-indigo-300 hover:shadow-sm transition-all ${proc.isFuture ? 'border-dashed border-amber-300 bg-amber-50/30' : 'border-gray-200'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-800 text-sm leading-snug">
            {proc.isFuture && <span className="text-amber-600 mr-1">[Planificat]</span>}
            {displayTitle}
          </p>
        </div>
        {hasAccess ? (
          proc.auctionId && link ? (
            <Link to={link}
              className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-700 bg-blue-50 border border-blue-300 px-3 py-1 rounded-lg hover:bg-blue-100 hover:border-blue-400 transition shrink-0"
              title="Vezi detalii procedura">
              {proc.auctionId} <ExternalLink size={13} />
            </Link>
          ) : proc.auctionId ? (
            <span className="text-sm font-bold text-gray-600 bg-gray-100 border border-gray-300 px-3 py-1 rounded-lg shrink-0">{proc.auctionId}</span>
          ) : null
        ) : (
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-lg shrink-0 blur-sm select-none">{proc.auctionId}</span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        {typeLabel && <span className="text-xs text-white bg-indigo-500 px-2 py-0.5 rounded font-medium">{typeLabel}</span>}
        {proc.contractType && (
          <span className="text-xs text-sky-700 bg-sky-50 border border-sky-200 px-2 py-0.5 rounded flex items-center gap-1">
            <Briefcase size={10} /> {proc.contractType}
          </span>
        )}
        {proc.procedureType && (
          <span className="text-xs text-orange-600 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded">{proc.procedureType}</span>
        )}
        {proc.fundingSource && (
          <span className="text-xs text-teal-600 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded flex items-center gap-1">
            <Wallet size={10} /> {proc.fundingSource}
          </span>
        )}
        {proc.cpvCode && (
          <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded" title={proc.cpvName}>CPV: {proc.cpvCode}</span>
        )}
        {(proc.startDate || proc.endDate) && (
          <span className="text-xs text-gray-500"><CalendarDays size={10} className="inline mr-0.5" />{proc.startDate} – {proc.endDate}</span>
        )}
        {proc.estimatedValue > 0 && (
          <span className="text-xs text-green-700 font-semibold bg-green-50 px-2 py-0.5 rounded">
            {proc.estimatedValue?.toLocaleString('ro-RO')} {proc.currency || currency || 'RON'}
          </span>
        )}
      </div>
      {proc.observations && (
        <p className="mt-1.5 text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 line-clamp-2">{proc.observations}</p>
      )}
    </div>
  );
};

const ProjectCard = ({ project }) => {
  const { hasActiveSubscription } = useAuth();
  const {
    id, projectTitle, authorityName, estimatedValue,
    durationExecution, startDate, endDate,
    currentProcedures, futureProcedures, summary,
    county, currency,
  } = project;

  const [showAllProcs, setShowAllProcs] = useState(false);
  const allProcedures = [...(currentProcedures || []), ...(futureProcedures || [])];
  const visibleProcs = showAllProcs ? allProcedures : allProcedures.slice(0, 3);
  const hasMoreProcs = allProcedures.length > 3;
  const sourceLabel = getProjectSourceLabel(project.groupingMetadata?.source);
  const projectStatus = summary?.projectStatus;
  const phases = summary?.phases || [];

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-blue-50 border border-indigo-200 rounded-3xl shadow-xl hover:shadow-2xl transition-all p-7 space-y-5 group">
      <div className="absolute -top-8 -right-8 opacity-10 pointer-events-none select-none">
        <BadgeEuro size={120} className="text-indigo-300" />
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="flex-1 min-w-0">
          <Link to={`/proiecte/${slugify(projectTitle)}/${id}`}
            className="text-2xl md:text-3xl font-extrabold text-indigo-800 hover:underline drop-shadow-sm">
            {projectTitle}
          </Link>
          {hasActiveSubscription ? (
            <p className="mt-1 text-sm text-gray-500 flex items-center gap-1.5 font-medium">
              <Building2 size={16} className="text-indigo-400 shrink-0" /> {authorityName}
              {county && <span className="ml-1 bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-0.5 rounded-full">{county}</span>}
            </p>
          ) : (
            <p className="mt-1 text-sm text-gray-400 flex items-center gap-1.5 italic">
              <Building2 size={16} className="text-gray-300 shrink-0" /> Disponibil cu abonament activ
            </p>
          )}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {projectStatus && (
              <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full border flex items-center gap-1 ${statusColors[projectStatus] || 'bg-gray-100 text-gray-700 border-gray-300'}`}>
                <Target size={11} /> {projectStatus}
              </span>
            )}
            {sourceLabel && <span className="bg-indigo-100 text-indigo-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">{sourceLabel}</span>}
            {project.paapYear > 0 && <span className="bg-yellow-100 text-yellow-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">PAAP {project.paapYear}</span>}
            {project.contractTypeName && <span className="bg-sky-100 text-sky-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">{project.contractTypeName}</span>}
            {project.procedureTypeName && <span className="bg-orange-100 text-orange-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">{project.procedureTypeName}</span>}
            {project.fundingSource && (
              <span className="bg-teal-100 text-teal-700 text-xs font-semibold px-2.5 py-0.5 rounded-full flex items-center gap-1">
                <Wallet size={11} /> {project.fundingSource}
              </span>
            )}
          </div>
        </div>
        <Link to={`/proiecte/${slugify(projectTitle)}/${id}`}
          className="mt-3 md:mt-0 inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-indigo-600 to-blue-500 text-white text-base font-semibold rounded-xl shadow-lg hover:from-indigo-700 hover:to-blue-600 transition shrink-0">
          Vezi detalii <ArrowRightCircle size={20} />
        </Link>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-base text-gray-700">
        <div className="flex items-center gap-3 bg-white/70 rounded-xl px-4 py-3 shadow-sm border border-green-100">
          <BadgeEuro size={24} className="text-green-600" />
          <div>
            <p className="text-gray-500 font-semibold text-xs">Valoare estimata</p>
            <span className="inline-block bg-green-100 text-green-700 text-sm font-bold px-2 py-0.5 rounded-md mt-0.5">
              {estimatedValue?.toLocaleString('ro-RO')} {currency || 'RON'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3 bg-white/70 rounded-xl px-4 py-3 shadow-sm border border-blue-100">
          <Clock3 size={24} className="text-blue-500" />
          <div>
            <p className="text-gray-500 font-semibold text-xs">Durata</p>
            <span className="inline-block bg-blue-100 text-blue-700 text-sm font-bold px-2 py-0.5 rounded-md mt-0.5">
              {durationExecution} luni
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3 bg-white/70 rounded-xl px-4 py-3 shadow-sm border border-purple-100">
          <CalendarDays size={24} className="text-purple-500" />
          <div>
            <p className="text-gray-500 font-semibold text-xs">Perioada</p>
            <span className="inline-block bg-purple-100 text-purple-700 text-sm font-bold px-2 py-0.5 rounded-md mt-0.5">
              {startDate} – {endDate}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3 bg-white/70 rounded-xl px-4 py-3 shadow-sm border border-indigo-100">
          <TrendingUp size={24} className="text-indigo-500" />
          <div>
            <p className="text-gray-500 font-semibold text-xs">Proceduri</p>
            <span className="inline-block bg-indigo-100 text-indigo-700 text-sm font-bold px-2 py-0.5 rounded-md mt-0.5">
              {summary?.activeProcedures || currentProcedures?.length || 0} active
              {(summary?.futureProcedures || futureProcedures?.length || 0) > 0 && (
                <span className="text-amber-600 ml-1">+ {summary?.futureProcedures || futureProcedures?.length} planificate</span>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Phase pipeline */}
      {phases.length > 0 && (
        <div className="flex flex-wrap gap-2 px-1">
          {phases.map((phase, i) => (
            <div key={i} className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${
              phase.status === 'Atribuit' ? 'bg-green-50 text-green-700 border-green-300' :
              phase.status === 'In licitatie' ? 'bg-blue-50 text-blue-700 border-blue-300' :
              'bg-amber-50 text-amber-700 border-amber-300'
            }`}>
              <span>{phaseIcons[phase.phase] || '📄'}</span>
              {phase.phase}
              <span className="text-[10px] opacity-70">({phase.procedureCount})</span>
              {phase.totalValue > 0 && (
                <span className="text-[10px] opacity-70 ml-0.5">
                  {(phase.totalValue / 1000).toFixed(0)}k
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Observatii */}
      {project.observations && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
          <MessageSquare size={16} className="text-amber-500 shrink-0 mt-0.5" />
          <div><span className="font-semibold">Observatii:</span> {project.observations}</div>
        </div>
      )}

      {/* Proceduri */}
      {allProcedures.length > 0 && (
        <div className="pt-4 border-t border-indigo-100">
          <p className="text-base font-semibold text-indigo-700 mb-3 flex items-center gap-2">
            <FileText size={20} className="text-indigo-400" /> Proceduri asociate
            <span className="ml-1 bg-indigo-100 text-indigo-700 text-xs font-bold px-2 py-0.5 rounded-full">
              {allProcedures.length}
            </span>
          </p>
          <div className="space-y-2">
            {visibleProcs.map((proc, idx) => (
              <ProcedureRow key={idx} proc={proc} currency={currency} hasAccess={hasActiveSubscription} />
            ))}
          </div>
          {hasMoreProcs && (
            <button onClick={() => setShowAllProcs(!showAllProcs)}
              className="mt-2 w-full text-center text-sm text-indigo-600 hover:text-indigo-800 font-semibold flex items-center justify-center gap-1 py-2 rounded-lg hover:bg-indigo-50 transition">
              {showAllProcs ? (
                <>Afiseaza mai putin <ChevronUp size={16} /></>
              ) : (
                <>Afiseaza toate ({allProcedures.length}) <ChevronDown size={16} /></>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectCard;
